/* @jsx h */
import { defineComponent, ref, h, computed, watch } from "vue";
import { useRoute, useRouter, RouterLink } from "vue-router";
import { api } from "@app/data/api";
import { TopProductsDatum, TopCategoriesDatum } from "@lemmonhq/types";
import { formatPriceInLocale } from "@lemmonhq/orders";
import { Loading } from "@app/vue/components/UIComponents";
import { format } from "date-fns";
import {
  DateRangePicker,
  type DateRange,
  type DateRangeOption,
  getDateRange,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";

export const DataDetailPage = defineComponent({
  name: "DataDetailPage",
  setup() {
    const auth = useAuth();
    const { t } = useLocalization();
    const route = useRoute();
    const router = useRouter();
    const loading = ref(true);
    const productsData = ref<TopProductsDatum[]>([]);
    const categoriesData = ref<TopCategoriesDatum[]>([]);
    const showRevenue = ref(true);
    const searchQuery = ref("");

    const dateRangeOptions = computed<DateRangeOption[]>(() => [
      { value: "this_week", label: t("reports.data_detail.this_week") },
      { value: "this_month", label: t("reports.data_detail.this_month") },
      { value: "this_quarter", label: t("reports.data_detail.this_quarter") },
      { value: "this_year", label: t("reports.data_detail.this_year") },
      { value: "last_12m", label: t("reports.data_detail.last_12_months") },
      { value: "custom", label: t("reports.data_detail.custom_range") },
    ]);

    const reportType = computed(() => route.query.type as string);
    const resolveInitialRange = (): DateRange => {
      const query = route.query;
      const start =
        typeof query.startDate === "string" ? query.startDate : undefined;
      const end = typeof query.endDate === "string" ? query.endDate : undefined;
      const rawType =
        typeof query.dateRangeType === "string"
          ? (query.dateRangeType as DateRange["type"])
          : undefined;

      const availableType = dateRangeOptions.value.some(
        (option) => option.value === rawType,
      )
        ? rawType
        : undefined;

      if (availableType === "custom" && start && end) {
        return { type: "custom", startDate: start, endDate: end };
      }

      const fallbackType =
        availableType && availableType !== "custom"
          ? availableType
          : "this_month";
      const computedRange = getDateRange(fallbackType as any);

      return {
        type: fallbackType as DateRange["type"],
        startDate: start ?? computedRange.startDate,
        endDate: end ?? computedRange.endDate,
      };
    };

    const selectedRange = ref<DateRange>(resolveInitialRange());
    const startDate = computed(() => selectedRange.value.startDate);
    const endDate = computed(() => selectedRange.value.endDate);
    const dateRangeType = computed(() => selectedRange.value.type);

    const pageTitle = computed(() => {
      return reportType.value === "categories"
        ? t("reports.data_detail.all_categories")
        : t("reports.data_detail.all_products");
    });

    const pageIcon = computed(() => {
      return reportType.value === "categories" ? "folder" : "trending_up";
    });

    const chipLabel = computed(() => {
      return reportType.value === "categories"
        ? t("reports.data_detail.category_detail")
        : t("reports.data_detail.product_detail");
    });

    const pageDescription = computed(() => {
      return reportType.value === "categories"
        ? t("reports.data_detail.category_desc")
        : t("reports.data_detail.product_desc");
    });

    const dateRangeLabel = computed(() => {
      const range = selectedRange.value;
      if (!range.startDate || !range.endDate) {
        return "—";
      }
      try {
        const start = format(new Date(range.startDate), "MMM d, yyyy");
        const end = format(new Date(range.endDate), "MMM d, yyyy");
        if (range.type === "custom") {
          return `${start} – ${end}`;
        }
        const preset = dateRangeOptions.value.find(
          (option) => option.value === range.type,
        );
        return preset
          ? `${preset.label} (${start} – ${end})`
          : `${start} – ${end}`;
      } catch {
        return "—";
      }
    });

    const metricLabel = computed(() =>
      showRevenue.value
        ? t("reports.data_detail.revenue")
        : t("reports.data_detail.count"),
    );

    const fetchData = async () => {
      loading.value = true;
      try {
        const dateRange = {
          type: dateRangeType.value,
          startDate: startDate.value,
          endDate: endDate.value,
        };

        if (reportType.value === "categories") {
          const data = await api.getTopCategories(dateRange as any);
          categoriesData.value = data;
        } else {
          const data = await api.getTopProducts(dateRange as any);
          productsData.value = data;
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        loading.value = false;
      }
    };

    const updateRangeQuery = (range: DateRange) => {
      const current = {
        startDate:
          typeof route.query.startDate === "string"
            ? route.query.startDate
            : "",
        endDate:
          typeof route.query.endDate === "string" ? route.query.endDate : "",
        dateRangeType:
          typeof route.query.dateRangeType === "string"
            ? route.query.dateRangeType
            : "",
      };

      const next = {
        startDate: range.startDate,
        endDate: range.endDate,
        dateRangeType: range.type,
      };

      if (
        current.startDate === next.startDate &&
        current.endDate === next.endDate &&
        current.dateRangeType === next.dateRangeType
      ) {
        return false;
      }

      const preservedEntries = Object.entries(route.query).filter(
        ([key, value]) =>
          typeof value === "string" &&
          !["startDate", "endDate", "dateRangeType"].includes(key),
      ) as [string, string][];

      router.replace({
        query: {
          ...Object.fromEntries(preservedEntries),
          ...next,
        },
      });

      return true;
    };

    const handleRangeChange = (range: DateRange | undefined) => {
      if (!range) return;
      selectedRange.value = range;
      const changed = updateRangeQuery(range);
      if (!changed) {
        fetchData();
      }
    };

    watch(
      () => route.query,
      () => {
        selectedRange.value = resolveInitialRange();
        fetchData();
      },
      { immediate: true },
    );

    if (
      typeof route.query.startDate !== "string" ||
      typeof route.query.endDate !== "string" ||
      typeof route.query.dateRangeType !== "string"
    ) {
      updateRangeQuery(selectedRange.value);
    }

    const downloadCSV = () => {
      const now = new Date();
      const dateStr = format(now, "yyyy-MM-dd HH:mm:ss");
      const periodStr = `${startDate.value} to ${endDate.value}`;

      let csvContent = `# Report Type: ${
        reportType.value === "categories" ? "Top Categories" : "Top Products"
      }\n`;
      csvContent += `# Date Range: ${periodStr}\n`;
      csvContent += `# Date Range Type: ${dateRangeType.value}\n`;
      csvContent += `# Generated: ${dateStr}\n\n`;

      if (reportType.value === "categories") {
        const headers = [
          "Rank",
          "Category Name",
          "Total Count",
          "Total Revenue",
        ];
        csvContent += headers.join(",") + "\n";

        const sortedData = [...categoriesData.value]
          .filter((el) => el.total_count > 0)
          .sort((a, b) => {
            if (showRevenue.value) {
              return b.total_revenue - a.total_revenue;
            } else {
              return b.total_count - a.total_count;
            }
          });

        sortedData.forEach((item, index) => {
          const row = [
            index + 1,
            `"${item.category_name}"`,
            item.total_count,
            (item.total_revenue / 100).toFixed(2),
          ];
          csvContent += row.join(",") + "\n";
        });
      } else {
        const headers = [
          "Rank",
          "Product Name",
          "Total Count",
          "Total Revenue",
        ];
        csvContent += headers.join(",") + "\n";

        const sortedData = [...productsData.value]
          .filter((el) => el.total_count > 0)
          .sort((a, b) => {
            if (showRevenue.value) {
              return b.total_revenue - a.total_revenue;
            } else {
              return b.total_count - a.total_count;
            }
          });

        sortedData.forEach((item, index) => {
          const row = [
            index + 1,
            `"${item.product.name}"`,
            item.total_count,
            (item.total_revenue / 100).toFixed(2),
          ];
          csvContent += row.join(",") + "\n";
        });
      }

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType.value}-report-${format(
        now,
        "yyyy-MM-dd",
      )}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };

    return () => {
      if (loading.value) {
        return (
          <div class="w-full h-[400px] flex justify-center items-center">
            <Loading />
          </div>
        );
      }

      const data =
        reportType.value === "categories"
          ? categoriesData.value
          : productsData.value;

      const sortedData = [...data]
        .filter((el: any) => {
          if (el.total_count === 0) return false;

          // Apply search filter
          if (searchQuery.value.trim()) {
            const query = searchQuery.value.toLowerCase();
            const name =
              reportType.value === "categories"
                ? el.category_name?.toLowerCase()
                : el.product?.name?.toLowerCase();
            return name?.includes(query);
          }

          return true;
        })
        .sort((a: any, b: any) => {
          if (showRevenue.value) {
            return b.total_revenue - a.total_revenue;
          }
          return b.total_count - a.total_count;
        });

      const totalRevenue = data.reduce(
        (sum: number, item: any) => sum + item.total_revenue,
        0,
      );
      const totalCount = data.reduce(
        (sum: number, item: any) => sum + item.total_count,
        0,
      );
      const hasRows = sortedData.length > 0;

      return (
        <div class="w-full flex flex-col gap-6 overflow-hidden">
          <section class="mx-6 mt-2 rounded-3xl border border-base-300/60 bg-gradient-to-br from-base-200/40 to-base-300/20 p-6 shadow-lg print:mx-0 print:mt-0 print:border-gray-300 print:bg-white print:shadow-none">
            <div class="flex flex-col gap-6 lg:flex-row items-start xl:justify-between">
              <div class="w-full flex flex-col gap-3 items-start sm:items-center flex-1">
                <div class="flex-1 w-full">
                  <div class="relative">
                    <span class="absolute inset-y-0 left-0 flex items-center pl-3">
                      <span class="material-symbols-outlined text-base-content/40">
                        search
                      </span>
                    </span>
                    <input
                      type="text"
                      placeholder={
                        reportType.value === "categories"
                          ? t("reports.data_detail.search_categories")
                          : t("reports.data_detail.search_products")
                      }
                      class="input input-bordered w-full pl-10"
                      value={searchQuery.value}
                      onInput={(e: any) => (searchQuery.value = e.target.value)}
                    />
                  </div>
                </div>
                <div class="w-full flex-1 flex gap-3 items-start justify-start">
                  <div class="join">
                    <button
                      class={`join-item btn btn-sm ${
                        showRevenue.value ? "btn-active" : ""
                      }`}
                      onClick={() => (showRevenue.value = true)}
                    >
                      {t("reports.data_detail.revenue")}
                    </button>
                    <button
                      class={`join-item btn btn-sm ${
                        !showRevenue.value ? "btn-active" : ""
                      }`}
                      onClick={() => (showRevenue.value = false)}
                    >
                      {t("reports.data_detail.count")}
                    </button>
                  </div>
                  <button
                    onClick={downloadCSV}
                    class="btn btn-sm btn-success gap-2"
                    title="Download CSV"
                  >
                    <span class="material-symbols-outlined text-sm">
                      download
                    </span>
                    <span class="hidden sm:inline">CSV</span>
                  </button>
                </div>
              </div>
              <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-end">
                <div class="hidden text-sm font-medium text-base-content/80 print:block">
                  {t("reports.data_detail.reporting_window")}{" "}
                  {dateRangeLabel.value}
                </div>
                <div class="w-full rounded-2xl print:hidden">
                  <div class="flex flex-col gap-4 items-end justify-end">
                    <label class="form-control flex items-end justify-end">
                      <div class="label pb-2 hidden">
                        <span class="label-text flex items-center gap-2 text-sm font-semibold text-base-content/80">
                          <span class="material-symbols-outlined text-2xl text-primary">
                            date_range
                          </span>
                          Date range
                        </span>
                      </div>
                      <DateRangePicker
                        options={dateRangeOptions.value}
                        value={selectedRange.value}
                        onChange={handleRangeChange}
                      />
                    </label>

                    <div class="rounded-xl bg-base-200/40 px-3 py-2 text-xs text-base-content/60 hidden">
                      {dateRangeLabel.value}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="flex-1 overflow-auto print:overflow-visible">
            <div class="px-6 pb-10 print:px-0 print:pb-0">
              {hasRows ? (
                <div class="overflow-hidden rounded-3xl border border-base-300/60 bg-base-200/20 shadow-lg">
                  <div class="flex items-center justify-between px-6 py-4 bg-base-200/70 text-xs font-semibold uppercase tracking-wide text-base-content/60">
                    <span>{t("reports.data_detail.name")}</span>
                    <span class="text-right">{metricLabel.value}</span>
                  </div>
                  <div class="divide-y divide-base-300/60">
                    {sortedData.map((item: any, index: number) => {
                      const value = showRevenue.value
                        ? item.total_revenue
                        : item.total_count;
                      const total = showRevenue.value
                        ? totalRevenue
                        : totalCount;
                      const percentage = total > 0 ? (value / total) * 100 : 0;
                      const name =
                        (reportType.value === "categories"
                          ? item.category_name
                          : item.product.name) ||
                        t("reports.data_detail.uncategorized");
                      const valueLabel = showRevenue.value
                        ? formatPriceInLocale(
                            item.total_revenue,
                            true,
                            2,
                            auth.user.value?.restaurant.currency || "CHF",
                          )
                        : item.total_count.toLocaleString();
                      const secondaryLabel = showRevenue.value
                        ? `${item.total_count.toLocaleString()} ${t("reports.data_detail.sold")}`
                        : `${t("reports.data_detail.revenue_prefix")} ${formatPriceInLocale(
                            item.total_revenue,
                            true,
                            2,
                            auth.user.value?.restaurant.currency || "CHF",
                          )}`;

                      return (
                        <div
                          key={
                            reportType.value === "categories"
                              ? item.category_id
                              : item.product.id
                          }
                          class="flex flex-row gap-4 bg-base-100/40 px-6 py-5 transition-colors hover:bg-base-100 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div class="flex items-center gap-4 min-w-0">
                            <span class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary text-sm font-semibold text-accent">
                              {index + 1}
                            </span>
                            <div class="min-w-0">
                              <h2 class="truncate text-accent" title={name}>
                                {name}
                              </h2>
                              <p class="text-xs text-base-content/60">
                                {item.total_count.toLocaleString()}{" "}
                                {reportType.value === "categories"
                                  ? t("reports.data_detail.items_sold")
                                  : t("reports.data_detail.units_sold")}
                              </p>
                            </div>
                          </div>
                          <div class="flex flex-col items-start gap-1 text-left sm:items-end sm:text-right">
                            <span class="text-lg text-accent">
                              {valueLabel}
                            </span>
                            {percentage > 0 && (
                              <span class="text-xs font-semibold text-primary">
                                {percentage.toFixed(1)}
                                {t("reports.data_detail.of_total")}{" "}
                                {metricLabel.value.toLowerCase()}
                              </span>
                            )}
                            <span class="text-xs text-base-content/60">
                              {secondaryLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div class="rounded-3xl border border-dashed border-base-300/60 bg-base-100/30 px-6 py-12 text-center text-base-content/60">
                  {t("reports.data_detail.no_data")}
                </div>
              )}
            </div>
          </section>
        </div>
      );
    };
  },
});
